import {ITestsService} from './ITestsService';
import {TestsRepository} from './TestsRepository';
import {
  Asset,
  AssetType,
  AssetUpdate,
  Question,
  Test,
  TestInsert,
  TestUpdate,
} from '../../graphql/API';
import type {QuestionWithNewFields} from '../Questions/QuestionTypes';
import {AssetsService} from '../Asset';
import {QuestionsService} from '../Questions';
import {AssetsRepository} from '../Asset/AssetsRepository';
import {
  DailyExerciseService,
  InitWithAmplify,
  InitEnvironmentVariable,
} from '@PikaDoApp/npm-pikado-logic';
import type {NewDailyExercise} from '@PikaDoApp/npm-pikado-logic';
import {Amplify} from 'aws-amplify';
import {getCurrentUser} from 'aws-amplify/auth';
import {getEnvironment} from '../../lib/getEnvironment';

// Extended interface to add asset field for UI compatibility
interface DailyExerciseWithAsset extends NewDailyExercise {
  asset?: {
    type: string;
    fileUri: string;
    thumbnailUri: string;
  };
}

export class TestsService implements ITestsService {
  testsRepository: TestsRepository;
  assetsService: AssetsService;
  questionsService: QuestionsService;
  assetsRepository: AssetsRepository;
  dailyExerciseService: DailyExerciseService;
  constructor() {
    this.testsRepository = new TestsRepository();
    this.assetsService = new AssetsService();
    this.questionsService = new QuestionsService();
    this.assetsRepository = new AssetsRepository();
    // Initialize Daily Exercise Service
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    this.dailyExerciseService = new DailyExerciseService();
  }

  // Helper method to transform MongoDB exercise data to GraphQL Test type
  private transformExerciseToTest(exercise: any): any {
    return {
      __typename: 'Test' as const,
      id: exercise._id,
      oldId: exercise.oldId,
      name: exercise.name || null,
      name_i18n: exercise.name_i18n || null,
      type: exercise.type,
      thumbnail: exercise.thumbnail,
      summary: exercise.summary || null,
      summary_i18n: exercise.summary_i18n || null,
      description: exercise.description || null,
      description_i18n: exercise.description_i18n || null,
      createdAt: exercise.createdAt,
      updatedAt: exercise.updatedAt,
      asset: {
        __typename: 'Asset' as const,
        id: exercise._id + '-asset', // Generate unique asset ID
        createdAt: exercise.createdAt,
        updatedAt: exercise.updatedAt,
        thumbnailUri: exercise.thumbnail || null,
        fileUri: exercise.media?.[0]?.url || null,
        type: this.mapMediaTypeToAssetType(exercise.media?.[0]?.type),
        title_i18n: null,
        subTitle_i18n: null,
        position: null,
      },
      questions: exercise.questions || null,
      lesson: null,
    };
  }

  // Helper method to map media type string to AssetType enum
  private mapMediaTypeToAssetType(mediaType?: string): AssetType | null {
    if (!mediaType) return null;
    const type = mediaType.toUpperCase();
    if (type === 'VIDEO') return AssetType.VIDEO;
    if (type === 'AUDIO') return AssetType.AUDIO;
    if (type === 'PHOTO' || type === 'IMAGE') return AssetType.PHOTO;
    if (type === 'PDF') return AssetType.PDF;
    return null;
  }

  // OLD CODE - Commented out as requested (using old getTests method)
  // async getTests(
  //   nextToken: string = null,
  //   scannedCount: number = null,
  // ): Promise<Array<Test> | any> {
  //   let tests = await this.testsRepository.getTests(nextToken, scannedCount);
  //   return tests;
  // }

  // NEW CODE - Using getAllDailyExercises from @PikaDoApp/npm-pikado-logic
  async getTests(page: number = 1,limit:number = 20): Promise<any> {
    try {
      const response = await this.dailyExerciseService.getAllDailyExercises(
        page,
        limit, // 20 exercises per page as requested
      );

      // Transform the response to match the expected format
      const items = response.data.map((exercise: any) =>
        this.transformExerciseToTest(exercise)
      );

      return {
        items,
        page: response.page,
        totalPages: response.totalPages,
        total: response.totalElements, // Fixed: API returns totalElements, not total
        // For backward compatibility, we can still include a nextToken concept
        nextToken: response.page < response.totalPages ? `page-${response.page + 1}` : null,
        scannedCount: response.totalElements, // Fixed: Using totalElements instead of total
      };
    } catch (error) {
      console.error('Error fetching daily exercises:', error);
      throw error;
    }
  }

  // OLD CODE - Commented out (using old getTestById method)
  // async getTestById(testId: string): Promise<Test> {
  //   return await this.testsRepository.getTestById(testId);
  // }

  // NEW CODE - Using getDailyExerciseById from @PikaDoApp/npm-pikado-logic
  async getTestById(testId: string): Promise<any> {
    try {
      const exercise = await this.dailyExerciseService.getDailyExerciseById(testId);

      // Transform media array to asset object for UI (preserves existing media as default values)
      let asset: any = undefined;
      if (exercise.media && exercise.media.length > 0) {
        const firstMedia = exercise.media[0];
        // Map media types: 'video' -> 'VIDEO', 'image' -> 'PHOTO', etc.
        let assetType = 'VIDEO'; // default
        if (firstMedia.type) {
          const mediaTypeUpper = firstMedia.type.toUpperCase();
          if (mediaTypeUpper === 'IMAGE' || mediaTypeUpper === 'PHOTO') {
            assetType = 'PHOTO';
          } else if (mediaTypeUpper === 'VIDEO') {
            assetType = 'VIDEO';
          } else if (mediaTypeUpper === 'AUDIO') {
            assetType = 'AUDIO';
          }
        }

        asset = {
          type: assetType,
          fileUri: firstMedia.url,
          thumbnailUri: exercise.thumbnail || firstMedia.url,
        };
      }

      // Transform the response to match the expected format
      return {
        id: exercise._id,
        _id: exercise._id,
        oldId: exercise.oldId,
        name: exercise.name,
        type: exercise.type,
        thumbnail: exercise.thumbnail,
        summary: exercise.summary,
        description: exercise.description,
        createdAt: exercise.createdAt,
        updatedAt: exercise.updatedAt,
        media: exercise.media,
        asset: asset,  // Populated with existing media to preserve on save
        // Map MongoDB question fields to GraphQL format
        questions: exercise.questions?.map((q: any) => {
          console.log('🔵 [TestsService.getTestById] Raw program from API:', q.program);
          const programId = q.program?._id || q.program || '';
          console.log('🔵 [TestsService.getTestById] Mapped program ID:', programId);
          return {
            ...q,
            id: q._id || q.id,
            text: q.questionText || q.text || '',
            textAI: q.AiText || q.textAI || '',
            program: programId,
          };
        }) || [],
      };

    } catch (error) {
      console.error('Error fetching daily exercise by id:', error);
      throw error;
    }
  }

  // OLD CODE - Commented out (using old createTest method)
  // async createTest(test: TestInsert | any): Promise<any> {
  //   try {
  //     let testObj = {
  //       name: test.name,
  //       assetId: '',
  //       questionIds: [],
  //       type: test.type,
  //       description: test.description,
  //       summary: test.summary,
  //       lessonId: test.lessonId,
  //     };
  //     console.log('inside create test');
  //     testObj.assetId = await this.assetsService.createAsset(test.asset!);
  //     testObj.questionIds = await Promise.all(
  //       test.questions?.map(async (q: any) => {
  //         return await this.questionsService.createQuestion(q);
  //       }),
  //     );
  //     // //TODO: Add validators
  //     return await this.testsRepository.createTest(testObj);
  //   } catch (e) {
  //     return new Promise((resolve, reject) =>
  //       reject(`Creating test error: ${e}`),
  //     );
  //   }
  // }

  // NEW CODE - Using createNewDailyExercise from @PikaDoApp/npm-pikado-logic
  async createTest(test: TestInsert | any): Promise<any> {
    try {
      // Get current user for createdBy field
      let createdBy = '';
      try {
        const user = await getCurrentUser();
        createdBy = user.signInDetails?.loginId || user.userId || 'unknown';
      } catch (error) {
        console.error('Error getting current user:', error);
        createdBy = 'unknown';
      }

      // STEP 1: Create questions FIRST to get question IDs
      console.log('Creating questions first...');
      const questionIds = await Promise.all(
        test.questions?.map(async (q: any) => {
          try {
            const questionId = await this.questionsService.createQuestion(q);
            return questionId;
          } catch (error) {
            console.error('Error creating question:', error);
            return null;
          }
        }) || []
      );

      // Filter out any failed question creations
      const validQuestionIds = questionIds.filter((id) => id !== null);
      console.log(`Created ${validQuestionIds.length} questions`);

      // STEP 2: Prepare media array from test.asset
      const media = test.asset?.fileUri ? [{
        type: test.asset.type?.toLowerCase() || 'video',
        url: test.asset.fileUri,
      }] : [];

      // STEP 3: Prepare daily exercise object for new API
      const dailyExerciseObj = {
        name: test.name,
        type: test.type,
        description: test.description,
        summary: test.summary,
        thumbnail: test.asset?.thumbnailUri || test.thumbnail || '',
        media: media, // NEW: Media array with asset data
        questions: validQuestionIds, // NEW: Question IDs array
        createdBy: createdBy,
        // Keep additional fields that may be needed
        oldId: test.oldId,
      };

      console.log('Creating daily exercise with pikado logic', {
        questionCount: validQuestionIds.length,
        hasMedia: media.length > 0,
      });
      const createdExercise = await this.dailyExerciseService.createNewDailyExercise(dailyExerciseObj);

      // Transform response
      return {
        id: createdExercise._id,
        _id: createdExercise._id,
        oldId: createdExercise.oldId,
        name: createdExercise.name,
        type: createdExercise.type,
        thumbnail: createdExercise.thumbnail,
        media: createdExercise.media,
        summary: createdExercise.summary,
        description: createdExercise.description,
        questions: createdExercise.questions,
        createdBy: createdExercise.createdBy,
        createdAt: createdExercise.createdAt,
        updatedAt: createdExercise.updatedAt,
      };
    } catch (e) {
      console.error('Creating daily exercise error:', e);
      return new Promise((resolve, reject) =>
        reject(`Creating daily exercise error: ${e}`),
      );
    }
  }

  // OLD CODE - Commented out (using old updateTest method)
  // async updateTest(test: Test, orginialTest: Test): Promise<any> {
  //   try {
  //     let testObj: TestUpdate = {
  //       name: test.name,
  //       type: test.type,
  //       description: test.description,
  //       summary: test.summary,
  //     };
  //
  //     console.log('inside update test');
  //     let newQuestionsIds = [];
  //     // testObj.assetId = await this.assetsService.createAsset(test.asset!);
  //     await Promise.all(
  //       test.questions?.map(async (q: Question) => {
  //         if (q.id) {
  //           return await this.questionsService.updateQuestion(
  //             q.id,
  //             q,
  //             q.updatedAt,
  //           );
  //         } else {
  //           if (!q.choices) {
  //             q.choices = [];
  //           }
  //           let questionId = await this.questionsService.createQuestion(q);
  //           newQuestionsIds.push(questionId);
  //         }
  //       }),
  //     );
  //
  //     if (
  //       !test?.asset?.id &&
  //       (test.asset?.fileUri || test.asset?.thumbnailUri)
  //     ) {
  //       testObj.assetId = await this.assetsRepository.createAsset(test.asset!);
  //     } else if (test?.asset?.id) {
  //       const assetUpdate: AssetUpdate = {
  //         type: test.asset.type,
  //         thumbnailUri: test.asset.thumbnailUri,
  //         fileUri: test.asset.fileUri,
  //         // title_i18n: test.asset.title_i18n,
  //         // subTitle_i18n: test.asset.subTitle_i18n,
  //         // position: test.asset.position,
  //       };
  //       await this.assetsRepository.updateAsset(
  //         test?.asset?.id,
  //         test?.asset?.updatedAt,
  //         assetUpdate,
  //       );
  //     }
  //     if (newQuestionsIds.length !== 0) {
  //       let questionIds = test.questions.map((q: Question) => q.id);
  //       testObj.questionIds = questionIds.concat(newQuestionsIds);
  //       testObj.questionIds = testObj.questionIds.filter((n) => n);
  //     } else {
  //       testObj.questionIds = test.questions.map((q: Question) => q.id);
  //     }
  //
  //     //TODO: Add validators
  //     return await this.testsRepository.updateTest(
  //       test.id,
  //       testObj,
  //       test.updatedAt,
  //     );
  //   } catch (e) {
  //     return new Promise((resolve, reject) =>
  //       reject(`update test error: ${e}`),
  //     );
  //   }
  // }

  // NEW CODE - Using updateDailyExercise from @PikaDoApp/npm-pikado-logic
  async updateTest(test: Test | any, orginialTest: Test): Promise<any> {
    try {
      // STEP 1: Handle question updates and new questions
      console.log('Processing questions for update...');
      const newQuestionsIds: string[] = [];
      const failedQuestions: Array<{id: string, error: any}> = [];

      const results = await Promise.all(
        test.questions?.map(async (q: Question | any, index: number) => {
          if (q.id || q._id) {
            // Existing question - update it
            try {
              console.log(`Updating question ${index + 1}/${test.questions.length} (ID: ${q._id || q.id}):`, {
                type: q.type,
                program: q.program,
                hasPayload: !!q.payload,
                hasBlanks: !!q.payload?.blanks,
                blanksCount: q.payload?.blanks?.length || 0
              });
              const result = await this.questionsService.updateQuestion(
                q._id || q.id,
                q,
                q.updatedAt,
              );
              console.log(`✅ Question ${index + 1} updated successfully`);
              return result;
            } catch (error) {
              console.error(`❌ Error updating question ${index + 1} (ID: ${q._id || q.id}):`, error);
              failedQuestions.push({ id: q._id || q.id, error });
              return null;
            }
          } else {
            // New question - create it
            try {
              if (!q.choices) {
                q.choices = [];
              }
              const questionId = await this.questionsService.createQuestion(q);
              newQuestionsIds.push(questionId);
              console.log(`✅ Question ${index + 1} created successfully`);
              return questionId;
            } catch (error) {
              console.error(`❌ Error creating question ${index + 1}:`, error);
              failedQuestions.push({ id: `new-question-${index}`, error });
              return null;
            }
          }
        }) || []
      );

      // Check if any questions failed to update
      if (failedQuestions.length > 0) {
        const errorMsg = `Failed to update ${failedQuestions.length} question(s). Check console for details.`;
        console.error('Failed questions:', failedQuestions);
        throw new Error(errorMsg);
      }

      // STEP 2: Collect all question IDs (existing + new)
      let questionIds: string[] = [];
      if (newQuestionsIds.length !== 0) {
        const existingQuestionIds = test.questions
          .map((q: Question | any) => q._id || q.id)
          .filter((id: string) => id);
        questionIds = existingQuestionIds.concat(newQuestionsIds);
        questionIds = questionIds.filter((n) => n); // Remove any nulls
      } else {
        questionIds = test.questions
          ?.map((q: Question | any) => q._id || q.id)
          .filter((id: string) => id) || [];
      }

      console.log(`Updated/created questions, total: ${questionIds.length}`);

      // STEP 3: Prepare media array from test.asset
      const media = test.asset?.fileUri ? [{
        type: test.asset.type?.toLowerCase() || 'video',
        url: test.asset.fileUri,
      }] : [];

      // STEP 4: Update daily exercise with new API
      // UpdateDailyExerciseInput now supports: id, questions, media, thumbnail, createdBy, name, type, summary, description
      console.log('Updating daily exercise with pikado logic', {
        questionCount: questionIds.length,
        hasMedia: media.length > 0,
      });

      const exerciseId = test._id || test.id;
      const updatedExercise = await this.dailyExerciseService.updateDailyExercise({
        id: exerciseId,
        questions: questionIds,
        media: media,
        thumbnail: test.asset?.thumbnailUri || test.thumbnail,
        name: test.name,
        type: test.type,
        summary: test.summary,
        description: test.description,
      });

      // Transform response
      return {
        id: updatedExercise._id,
        _id: updatedExercise._id,
        oldId: updatedExercise.oldId,
        name: updatedExercise.name,
        type: updatedExercise.type,
        thumbnail: updatedExercise.thumbnail,
        media: updatedExercise.media,
        summary: updatedExercise.summary,
        description: updatedExercise.description,
        questions: updatedExercise.questions,
        createdBy: updatedExercise.createdBy,
        createdAt: updatedExercise.createdAt,
        updatedAt: updatedExercise.updatedAt,
      };
    } catch (e) {
      console.error('Updating daily exercise error:', e);
      return new Promise((resolve, reject) =>
        reject(`update daily exercise error: ${e}`),
      );
    }
  }

  // Delete daily exercise using deleteDailyExercise from @PikaDoApp/npm-pikado-logic
  async deleteTest(testId: string): Promise<any> {
    try {
      console.log('Deleting daily exercise:', testId);
      const result = await this.dailyExerciseService.deleteDailyExercise(testId);

      return {
        success: true,
        message: 'Daily exercise deleted successfully',
        deletedId: testId,
        result,
      };
    } catch (error) {
      console.error('Error deleting daily exercise:', error);
      return new Promise((resolve, reject) =>
        reject(`Delete daily exercise error: ${error}`),
      );
    }
  }

  // OLD CODE - Using old GraphQL API (backward compatibility for existing code)
  async getTestByName(name: string): Promise<Array<Test>> {
    return await this.testsRepository.getTestsByName(name);
  }

  // NEW CODE - Using searchDailyExercisesByName from @PikaDoApp/npm-pikado-logic
  async searchTestsByName(
    name: string,
    page: number = 1,
    limit: number = 20,
  ): Promise<{
    items: Array<Test>;
    page: number;
    totalPages: number;
    total: number;
  }> {
    try {
      const response = await this.dailyExerciseService.searchDailyExercisesByName(
        name,
        page,
        limit,
      );

      // Transform the response to match the expected format
      const items = response.data.map((exercise: any) =>
        this.transformExerciseToTest(exercise)
      );

      return {
        items,
        page: response.page,
        totalPages: response.totalPages,
        total: response.totalElements,
      };
    } catch (error) {
      console.error('Error searching daily exercises by name:', error);
      return {
        items: [],
        page: 1,
        totalPages: 0,
        total: 0,
      };
    }
  }
}
